import React, { ReactNode, useState, useRef, useEffect } from "react";
import { HiXMark, HiPencil, HiEllipsisVertical } from "react-icons/hi2";

export interface LogCardProps {
  children: ReactNode;
  onDelete?: () => void;
  onEdit?: () => void;
  backgroundColor?: string;
  className?: string;
}

const LogCard: React.FC<LogCardProps> = ({
  children,
  onDelete,
  onEdit,
  backgroundColor,
  className,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleDropdownClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDropdown((prev) => !prev);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDropdown(false);
    if (onEdit) onEdit();
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDropdown(false);
    if (onDelete) onDelete();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  return (
    <div
      className={`
        mb-1.5 relative px-3 py-2.5 rounded-lg border border-transparent
        transition-colors duration-150 hover:border-base-300
        ${className || ""}
      `}
      style={backgroundColor ? { backgroundColor } : {}}
    >
      {/* Dropdown menu positioned absolutely in top-right */}
      {(onEdit || onDelete) && (
        <div className="absolute top-1.5 right-1.5 z-10" ref={dropdownRef}>
          <button
            onClick={handleDropdownClick}
            className="p-1.5 hover:bg-base-200 rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            aria-label="Log options"
          >
            <HiEllipsisVertical className="w-4 h-4 text-base-content/50" />
          </button>

          {/* Dropdown menu */}
          {showDropdown && (
            <div className="absolute right-0 bottom-full mb-1 bg-base-100 border border-base-200 rounded-lg shadow-lg z-20 min-w-[120px] overflow-hidden">
              {onEdit && (
                <button
                  onClick={handleEditClick}
                  className="w-full px-3 py-2 text-left text-sm text-base-content/80 hover:bg-base-200 flex items-center gap-2 transition-colors focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary"
                >
                  <HiPencil className="w-3.5 h-3.5" />
                  Edit
                </button>
              )}
              {onDelete && (
                <button
                  onClick={handleDeleteClick}
                  className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-500/10 flex items-center gap-2 transition-colors focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-red-500"
                >
                  <HiXMark className="w-3.5 h-3.5" />
                  Delete
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className="relative">{children}</div>
    </div>
  );
};

export default LogCard;
